<script setup lang="ts">
import {
  absMoney,
  absRatio,
  formatMoney,
  formatRatio,
  MINUS_SIGN,
  PLUS_SIGN,
  RATIO_SCALE,
} from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { KpiChange, PercentChange } from '../internal/kpi-change.ts'
import { changeVerdict, type Favourable } from '../internal/verdict.ts'

const props = withDefaults(
  defineProps<{
    change: KpiChange | PercentChange
    favourable?: Favourable
    caption?: string | undefined
    percentDigits?: number
  }>(),
  {
    favourable: 'up',
    caption: undefined,
    percentDigits: 2,
  },
)
const { t, locale } = useI18n()
const localeCode = computed(() => locale.value as 'en' | 'hu')

const roundsToZero = computed(() => {
  const percent = props.change.percent
  return (
    percent !== undefined && absRatio(percent).units * 2n < 10n ** BigInt(RATIO_SCALE - props.percentDigits)
  )
})
const sign = computed(() => {
  if (roundsToZero.value) return ''
  return props.change.direction < 0 ? MINUS_SIGN : props.change.direction > 0 ? PLUS_SIGN : ''
})
const verdict = computed(() => changeVerdict(props.change, props.favourable))
// The pill is a tint of the verdict's own colour, never the colour carried by text alone.
const VERDICT_CLASSES = {
  good: 'bg-[color:color-mix(in_srgb,var(--color-positive)_12%,transparent)] text-[color:var(--color-positive)]',
  bad: 'bg-[color:color-mix(in_srgb,var(--color-negative)_12%,transparent)] text-[color:var(--color-negative)]',
  flat: 'bg-[color:var(--color-surface-sunken)] text-[color:var(--color-text-muted)]',
} as const
const magnitudeText = computed(() => {
  if (props.change.percent !== undefined)
    return `${formatRatio(absRatio(props.change.percent), {
      locale: localeCode.value,
      minimumFractionDigits: props.percentDigits,
      maximumFractionDigits: props.percentDigits,
    })}%`
  return 'delta' in props.change
    ? formatMoney(absMoney(props.change.delta), { locale: localeCode.value })
    : ''
})
</script>

<template>
  <p class="flex min-w-0 items-center gap-[var(--space-inline)] text-[length:var(--text-step-1)]">
    <span
      class="inline-flex shrink-0 items-center gap-[var(--space-hair)] rounded-full py-px pl-[var(--space-tight)] pr-[var(--space-inline)] font-semibold tabular-nums"
      :class="VERDICT_CLASSES[verdict]"
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 16 16"
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <path v-if="verdict === 'flat'" d="M2.6 9.2c1.8-2.4 3.6-2.4 5.4 0s3.6 2.4 5.4 0" />
        <path v-else-if="change.direction > 0" d="M8 13V3.6M4 7.4l4-4 4 4" />
        <path v-else d="M8 3v9.4M4 8.6l4 4 4-4" />
      </svg>
      {{ sign }}{{ magnitudeText }}
    </span>
    <span class="truncate text-[color:var(--color-text-muted)]">{{
      caption ?? ('days' in change ? t('dashboard.changeOverDays', { days: change.days }) : '')
    }}</span>
  </p>
</template>
